import React, { useEffect, useRef, useState } from 'react';

//Third Party
import PropTypes from 'prop-types';
import cookie from 'react-cookie';
import Recaptcha from 'react-google-invisible-recaptcha';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, HelpBlock } from 'react-bootstrap';

// Twine Components
import ButtonWithLoader from '../ButtonWithLoader/ButtonWithLoader';

// Redux
import {
  signUp,
  resetEmailError,
  validateEmail,
} from '../../redux/modules/auth';
import { trackEvent } from '../../helpers/Analytics';

// Styles
import styles from './BriefDetailsSignUp.module.scss';

const BriefDetailsOnboarding = ({ text, signUpContainerClass }) => {
  const [email, setEmail] = useState('');
  const [signUpDetails, setSignUpDetails] = useState({});
  const [disabledState, setDisabledState] = useState(false);
  const [emailWarningError, setEmailWarningError] = useState('');
  const [emailDangerError, setEmailDangerError] = useState('');
  const [emailInputDanger, setEmailInputDanger] = useState(false);

  const selectAuth = (state) => state.auth;
  const selectEmailError = (state) => state.auth.emailError;
  const authState = useSelector(selectAuth);
  const emailError = useSelector(selectEmailError);

  const { emailValidationError, emailValidationType } = authState;

  const recaptchaRef = useRef(null);
  const signUpDetailsRef = useRef(signUpDetails);

  const dispatch = useDispatch();

  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setEmail(newEmail);
    setEmailWarningError('');
    setEmailDangerError('');
    setEmailInputDanger(false);
    dispatch(resetEmailError());
  };

  useEffect(() => {
    setDisabledState(authState.signingUp);
  }, [authState.signingUp]);

  // Existing user error
  useEffect(() => {
    if (emailError.length > 0) {
      setEmailDangerError(emailError[0]?.toString());
    }
  }, [emailError]);

  useEffect(() => {
    if (emailValidationType === 'Invalid') {
      setEmailWarningError('');
      setEmailDangerError('');
      setEmailDangerError(emailValidationError);
    } else {
      setEmailWarningError('');
      setEmailDangerError('');
      setEmailWarningError(emailValidationError);
    }
  }, [emailValidationError, emailValidationType]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setEmailWarningError('');
      setEmailDangerError('');
      dispatch(resetEmailError());

      if (email !== '') {
        if (email.includes('@') && email.includes('.')) {
          dispatch(validateEmail(email));
        }
      }
    }, 250);

    return () => clearTimeout(delayDebounceFn);
  }, [email]);

  const validateEmailFormat = () => {
    if (email === '') {
      setEmailWarningError('');
      setEmailDangerError('');
      setEmailDangerError('Please enter your email address');
      setEmailInputDanger(true);
      return false;
    }

    if (!email.includes('@') || !email.includes('.')) {
      setEmailWarningError('');
      setEmailDangerError('');
      setEmailDangerError("Email doesn't contain @ or .");
      setEmailInputDanger(true);
      return false;
    }

    if (emailDangerError) {
      setEmailInputDanger(true);
      return false;
    }

    return true;
  };

  const handleCreateAccount = () => {
    const isValidEmail = validateEmailFormat();

    if (
      !emailDangerError &&
      isValidEmail &&
      emailValidationType !== 'Invalid'
    ) {
      trackEvent('ab-test-brief-details-apply-button-click');
      const removeNonAlphanumeric = (str) => {
        return str.replace(/[^a-zA-Z0-9]/g, '');
      };

      if (!email) {
        return;
      }

      const usernameArray = email.split('@');

      // Adds 4 random digits to the end of the username
      const randomlyGeneratedUsername =
        removeNonAlphanumeric(usernameArray[0]) +
        Math.floor(Math.random() * 9000 + 1000);

      // We can't have usernames longer than 20 characters
      const trimmedUsername = randomlyGeneratedUsername.slice(0, 20);

      const randomPassword = window.crypto
        .getRandomValues(new BigUint64Array(1))[0]
        .toString(36);

      const generatedSignUpDetails = {
        email: email,
        password: randomPassword,
        type: 'seller',
        username: trimmedUsername,
        utm: {},
        ai_collector: false,
      };

      if (typeof cookie.load('utm_source') !== 'undefined') {
        generatedSignUpDetails.utm.source = cookie.load('utm_source');
      }

      if (typeof cookie.load('utm_medium') !== 'undefined') {
        generatedSignUpDetails.utm.medium = cookie.load('utm_medium');
      }

      if (typeof cookie.load('utm_campaign') !== 'undefined') {
        generatedSignUpDetails.utm.campaign = cookie.load('utm_campaign');
      }

      if (typeof cookie.load('utm_term') !== 'undefined') {
        generatedSignUpDetails.utm.term = cookie.load('utm_term');
      }

      if (typeof cookie.load('utm_content') !== 'undefined') {
        generatedSignUpDetails.utm.content = cookie.load('utm_content');
      }

      if (recaptchaRef.current) {
        setSignUpDetails(generatedSignUpDetails);
      } else {
        dispatch(signUp(generatedSignUpDetails));
      }
    }
  };

  useEffect(() => {
    signUpDetailsRef.current = signUpDetails;
    if (Object.keys(signUpDetails).length > 0) {
      recaptchaRef.current.execute();
    }
  }, [signUpDetails]);

  const handleRecaptchaResolved = () => {
    //had issues trying to access the signUpDetails state here
    //chatGPT suggested using a Ref instead and it worked
    signUpDetailsRef.current.recaptcha = recaptchaRef.current.getResponse();
    dispatch(signUp(signUpDetailsRef.current));
    recaptchaRef.current.reset();
  };

  const ExistingUserWarning = () => {
    return (
      <>
        That email already exists in our platform, would you like to{' '}
        <a href="/signin">sign-in?</a>
      </>
    );
  };

  return (
    <>
      <div className={signUpContainerClass}>
        <div className={styles.inputContainer}>
          <FormControl
            type="email"
            placeholder="e.g. name@company.com"
            disabled={false}
            value={email}
            onChange={(event) => handleEmailChange(event)}
            className={[styles.input, emailInputDanger && styles.inputDanger]}
          />
        </div>
        <ButtonWithLoader
          bsStyle="primary"
          disabled={disabledState}
          style={{ minWidth: 75 }}
          onClick={() => handleCreateAccount()}
          loading={authState.signingUp}
          text={text}
        />
      </div>
      {!emailWarningError && !emailDangerError && <HelpBlock>&nbsp;</HelpBlock>}
      {emailWarningError && (
        <HelpBlock className={styles.emailWarning}>
          {emailWarningError}
        </HelpBlock>
      )}
      {emailDangerError && (
        <HelpBlock className={styles.emailDanger}>
          {emailDangerError?.includes('email has already been taken') ? (
            <ExistingUserWarning />
          ) : (
            emailDangerError
          )}
        </HelpBlock>
      )}
      <p className={styles.termsText}>
        By applying, you agree to our <a href="/terms">Terms</a>.
      </p>
      {process.env.GOOGLE_RECAPTCHA_API_KEY && (
        <Recaptcha
          ref={recaptchaRef}
          sitekey={process.env.GOOGLE_RECAPTCHA_API_KEY}
          onResolved={handleRecaptchaResolved}
        />
      )}
    </>
  );
};

BriefDetailsOnboarding.propTypes = {
  text: PropTypes.string,
  signUpContainerClass: PropTypes.string,
};

export default BriefDetailsOnboarding;
